
import React, { useMemo, useState } from 'react';
import { AvaliableCalendar } from '@/components/ui/avaliable-calendar';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';
import { getDateAvailability } from './utils/dateAvailability';
import { DateDetails } from './DateDetails';
import { TeacherAvailabilityException, WeeklySchedule } from '@/types/teacherAvailability';

interface MonthlyViewProps {
  weeklySchedule: WeeklySchedule;
  exceptions: TeacherAvailabilityException[];
  teacherCode?: string;
  onExceptionUpdate?: () => void;
}

export function MonthlyView({ weeklySchedule, exceptions, teacherCode, onExceptionUpdate }: MonthlyViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [month, setMonth] = useState<Date>(new Date());

  // Custom day content for calendar - recalculate when month changes
  const modifiers = useMemo(() => {
    const available: Date[] = [];
    const exception: Date[] = [];
    
    // Get the displayed month from the calendar state
    const displayedMonth = month.getMonth();
    const displayedYear = month.getFullYear();
    
    // Calculate for the displayed month, not current month
    for (let day = 1; day <= 31; day++) {
      try {
        const date = new Date(displayedYear, displayedMonth, day);
        if (date.getMonth() !== displayedMonth) continue;
        
        const availability = getDateAvailability(date, weeklySchedule, exceptions);
        if (availability.status === 'available') {
          available.push(date);
        } else if (availability.status === 'exception') {
          exception.push(date);
        }
      } catch (e) {
        // Invalid date, skip
      }
    }
    
    return { available, exception };
  }, [weeklySchedule, exceptions, month]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col xs:flex-row items-start xs:items-center gap-2 xs:gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded flex-shrink-0"></div>
          <span className="text-xs xs:text-sm">วันที่รับงานสอน</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-orange-500 rounded flex-shrink-0"></div>
          <span className="text-xs xs:text-sm">วันหยุดการสอน (ชั่วคราว)</span>
        </div>
      </div>
      
      <Card className="p-2 xs:p-4">
        <div className="calendar-mobile-fix">
          <AvaliableCalendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={month}
            onMonthChange={setMonth}
            modifiers={modifiers}
            modifiersClassNames={{
              available: "bg-green-100 text-green-800 hover:bg-green-200",
              exception: "bg-orange-100 text-orange-800 hover:bg-orange-200"
            }}
            className="rounded-md border w-full"
          />
        </div>
      </Card>

      {selectedDate && (
        <Card className="p-3 xs:p-4">
          <h4 className="font-medium mb-2 text-sm xs:text-base">
            รายละเอียดวันที่ {format(selectedDate, 'dd/MM/yyyy')}
          </h4>
          <DateDetails 
            date={selectedDate} 
            weeklySchedule={weeklySchedule}
            exceptions={exceptions}
            teacherCode={teacherCode}
            onExceptionUpdate={onExceptionUpdate}
          />
        </Card>
      )}
    </div>
  );
}
